import { ImageUp } from "lucide-react";
import { useEffect, useState } from "react";
import { uploadPhoto } from "../services/api";

interface PhotoUploaderProps {
  userId: string;
  disabled: boolean;
  onUploaded: () => void;
}

const maxPhotoBytes = 4 * 1024 * 1024;
const allowedTypes = ["image/jpeg"];

export default function PhotoUploader({ userId, disabled, onUploaded }: PhotoUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function handleUpload() {
    if (!file) {
      return;
    }

    const validation = validateFile(file);
    if (validation) {
      setError(validation);
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);

    try {
      await uploadPhoto(userId, file);
      setMessage("Photo uploaded.");
      setFile(null);
      onUploaded();
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Could not upload photo.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="inline-tool" aria-label="Profile photo">
      <div className="inline-tool-heading">
        <ImageUp size={18} aria-hidden="true" />
        <h3>Profile photo</h3>
      </div>

      <div className="photo-grid">
        <div className="photo-preview">{previewUrl ? <img src={previewUrl} alt="Selected profile" /> : <span>No preview</span>}</div>
        <div className="photo-controls">
          <input
            type="file"
            accept="image/jpeg"
            disabled={disabled || uploading}
            onChange={(event) => {
              const selected = event.target.files?.[0] || null;
              setFile(selected);
              setError(selected ? validateFile(selected) : null);
              setMessage(null);
            }}
          />
          <button type="button" className="secondary-button" onClick={handleUpload} disabled={!file || disabled || uploading || Boolean(error)}>
            <ImageUp size={16} aria-hidden="true" />
            {uploading ? "Uploading" : "Upload"}
          </button>
        </div>
      </div>

      {message ? <div className="alert alert-success">{message}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
    </section>
  );
}

function validateFile(file: File): string | null {
  if (!allowedTypes.includes(file.type)) {
    return "Profile photo must be a JPEG image.";
  }

  if (file.size > maxPhotoBytes) {
    return "Profile photos must be 4 MB or smaller.";
  }

  return null;
}
